const clockFormatter = new Intl.DateTimeFormat('zh-CN', {
  timeZone: 'Asia/Shanghai',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
});

function parts(iso: string | null | undefined) {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  const list = clockFormatter.formatToParts(date);
  const get = (type: string) => list.find((p) => p.type === type)?.value ?? '';
  return {
    year: get('year'),
    month: get('month'),
    day: get('day'),
    hour: get('hour'),
    minute: get('minute'),
    second: get('second'),
  };
}

export function formatClock(iso: string | null | undefined): string {
  const p = parts(iso);
  if (!p) return '';
  return `${p.year}年${p.month}月${p.day}日 ${p.hour}:${p.minute}:${p.second}`;
}

export function formatTime(iso: string | null | undefined): string {
  const p = parts(iso);
  if (!p) return '--:--:--';
  return `${p.hour}:${p.minute}:${p.second}`;
}

export function formatDate(iso: string | null | undefined): string {
  const p = parts(iso);
  if (!p) return '--';
  return `${p.year}-${p.month}-${p.day}`;
}

export function formatDuration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || seconds <= 0) return '00:00';
  const s = Math.round(seconds);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const mm = String(m).padStart(2, '0');
  const ss = String(sec).padStart(2, '0');
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}

export function formatRows(n: number | null | undefined): string {
  if (!n) return '0';
  return n.toLocaleString('zh-CN');
}

export function formatImported(iso: string | null | undefined): string {
  const p = parts(iso);
  if (!p) return '--';
  return `${p.month}-${p.day} ${p.hour}:${p.minute}`;
}

export function formatDay(iso: string | null | undefined): string {
  const p = parts(iso);
  if (!p) return '--';
  return `${p.month} 月 ${p.day} 日`;
}

export function formatTimeShort(iso: string | null | undefined): string {
  const p = parts(iso);
  if (!p) return '--:--';
  return `${p.hour}:${p.minute}`;
}

/** 日志行用的短时间：今天只显示时分秒，跨天补月日。 */
export function formatLogTime(iso: string | null | undefined): string {
  const p = parts(iso);
  if (!p) return '--:--:--';
  const now = parts(new Date().toISOString());
  if (now && p.year === now.year && p.month === now.month && p.day === now.day) {
    return `${p.hour}:${p.minute}:${p.second}`;
  }
  return `${p.month}-${p.day} ${p.hour}:${p.minute}`;
}

export function formatDateTime(iso: string | null | undefined): string {
  const p = parts(iso);
  if (!p) return '--';
  return `${p.year}-${p.month}-${p.day} ${p.hour}:${p.minute}`;
}

export function timeAgo(iso: string | null | undefined): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const diff = Date.now() - date.getTime();
  if (diff < 60_000) return '刚刚';
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 60) return `${minutes} 分钟前`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} 小时前`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days} 天前`;
  return formatDateTime(iso);
}

export function greeting(): string {
  const hour = Number(parts(new Date().toISOString())?.hour ?? '0');
  if (hour < 6) return '凌晨好';
  if (hour < 12) return '上午好';
  if (hour < 14) return '中午好';
  if (hour < 18) return '下午好';
  return '晚上好';
}